import { Download, UserCheck, UserX } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { download } from '../../api/client'
import { useCancelRegistration, useRegistrations } from '../../api/hooks'
import type { Event } from '../../api/types'
import { Badge, Button, Card, EmptyState, Input, LiveDot, Select, TableSkeleton } from '../../components/ui'
import { useToast } from '../../components/ui/toast'
import { CUSTOMER_TYPE_LABEL, fmtDateTime } from '../../lib/format'

export function ParticipantsTab({ event: e, isManager }: { event: Event; isManager: boolean }) {
  const regs = useRegistrations(e.id)
  const cancel = useCancelRegistration(e.id)
  const toast = useToast()
  const [params, setParams] = useSearchParams()
  const [q, setQ] = useState('')
  const team = params.get('team') ?? ''
  const category = params.get('category') ?? ''
  const setTeam = (v: string) => { const n = new URLSearchParams(params); if (v) n.set('team', v); else n.delete('team'); n.delete('category'); setParams(n, { replace: true }) }
  const [showCancelled, setShowCancelled] = useState(false)

  const all = regs.data ?? []
  const teams = useMemo(() => Array.from(new Map(all.filter((r) => r.team_code).map((r) => [r.team_code!, `${r.team_code} ${r.team_name}`])).entries()).sort(), [all])
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase()
    return all.filter((r) =>
      (showCancelled || r.status === 'ANMALD') &&
      (!team || r.team_code === team) &&
      (!category || r.category_id === category) &&
      (!s || `${r.first_name} ${r.last_name} ${r.email} ${r.company ?? ''}`.toLowerCase().includes(s)),
    )
  }, [all, q, team, category, showCancelled])
  const categoryName = category ? all.find((r) => r.category_id === category)?.category_name : null
  const active = all.filter((r) => r.status === 'ANMALD').length

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-500">
          {isManager ? `${active} anmälda av ${e.capacity} platser. Rådgivare ser enbart sitt eget team.` : `${active} anmälda från ditt team.`}
        </p>
        <div className="flex items-center gap-4">
          <LiveDot />
          <Button size="sm" icon={<Download className="size-4" />} onClick={() => download(`/api/events/${e.id}/registrations/export.csv`, `deltagare_${e.name}.csv`)} disabled={!all.length}>
            Exportera deltagarlista
          </Button>
        </div>
      </div>

      {regs.isLoading ? <Card padded={false}><TableSkeleton rows={8} cols={7} /></Card> : all.length === 0 ? (
        <EmptyState icon={<UserCheck className="size-8" />} title="Inga anmälningar ännu" description="Anmälningar dyker upp här i realtid när kunder klickar på sin länk i inbjudan." />
      ) : (
        <Card padded={false} title={
          <div className="flex flex-wrap items-center gap-2">
            <Input placeholder="Sök namn, e-post, företag…" value={q} onChange={(ev) => setQ(ev.target.value)} className="h-8 w-60 text-xs" />
            {isManager && teams.length > 1 && (
              <Select value={team} onChange={(ev) => setTeam(ev.target.value)} className="h-8 w-52 text-xs">
                <option value="">Alla team</option>
                {teams.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </Select>
            )}
            <label className="ml-1 inline-flex items-center gap-1.5 text-xs text-ink-500"><input type="checkbox" checked={showCancelled} onChange={(ev) => setShowCancelled(ev.target.checked)} />Visa avanmälda</label>
            {category && <Badge tone="navy">Kategori: {categoryName ?? '…'} <button className="ml-1 opacity-70 hover:opacity-100" onClick={() => setTeam(team)} aria-label="Ta bort filter">×</button></Badge>}
          </div>
        } actions={<span className="text-xs text-ink-500">{rows.length} rader</span>}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px]">
              <thead className="table-head"><tr><th>Namn</th><th>E-post</th><th>Team</th><th>Typ</th><th>Företag</th><th>Kost</th><th>Anmäld</th><th></th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={`table-row ${r.status === 'AVANMALD' ? 'opacity-50' : ''}`}>
                    <td className="font-medium text-ink-900">
                      {r.first_name} {r.last_name}
                      {r.matched_customer ? <span title="Matchad mot kund i Dynamics" className="ml-1.5 inline-block size-1.5 rounded-full bg-success-500 align-middle" /> : <span title="Okänd – ej i CRM" className="ml-1.5 inline-block size-1.5 rounded-full bg-ink-300 align-middle" />}
                      {r.status === 'AVANMALD' && <Badge tone="neutral" className="ml-2">Avanmäld</Badge>}
                    </td>
                    <td className="text-ink-500">{r.email}</td>
                    <td>{r.team_code ? `${r.team_code} ${r.team_name}` : <span className="text-ink-500">Manuell</span>}</td>
                    <td>{r.customer_type ? <Badge tone={r.customer_type === 'KUND' ? 'success' : 'copper'}>{CUSTOMER_TYPE_LABEL[r.customer_type]}</Badge> : '–'}</td>
                    <td className="text-ink-500">{r.company ?? '–'}</td>
                    <td className="text-ink-500">{r.dietary ?? '–'}</td>
                    <td className="tabular-nums text-ink-500">{fmtDateTime(r.registered_at)}</td>
                    <td className="text-right">
                      {isManager && r.status === 'ANMALD' && (
                        <button title="Avanmäl" className="rounded p-1 text-ink-400 hover:bg-danger-50 hover:text-danger-600" onClick={() => cancel.mutate(r.id, { onSuccess: () => toast.push('info', `${r.first_name} ${r.last_name} avanmäldes`) })}>
                          <UserX className="size-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}
